/**
 * DV-FF-002: Feature Flags Console
 *
 * Admin-only console for managing feature flags with toggles,
 * status indicators, audit logging, and one-click status report.
 * Updated for DV-INF-002: Uses HTTP endpoints with unified error handling.
 */

import { useState, useMemo, useCallback } from 'react';
import {
  Settings,
  Search,
  Filter,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Eye,
  Edit,
  Loader2,
  Copy,
  Check
} from 'lucide-react';
import { cn } from '@/utils/cn';
import {
  useFeatureFlagsWithStatus,
  useUpdateFeatureFlag,
  useFeatureFlagAuditLog,
  useFeatureFlagsAdminCheck
} from '@/hooks/useFeatureFlags';
import type {
  FeatureFlagWithStatus,
  FeatureFlagUpdate,
  FeatureFlagsFilters,
  LogEntry
} from '@/types/feature-flags';
import { PermissionNotice, NotFoundNotice, ServerErrorNotice, ConflictNotice } from './ErrorNotices';
import { useStatusReport } from './useStatusReport';
import { getDemoError, logDemoError } from '@/lib/demo-error-handler';
import { featureFlagsAPI } from '@/lib/feature-flags-api';

export interface FeatureFlagsConsoleProps {
  onLog?: (entry: LogEntry) => void;
  className?: string;
  'data-testid'?: string;
}

// Feature flag toggle component
interface FeatureFlagToggleProps {
  flag: FeatureFlagWithStatus;
  field: 'enabled' | 'admin_enabled';
  onUpdate: (update: FeatureFlagUpdate) => Promise<void>;
  isUpdating?: boolean;
  'data-testid'?: string;
}

function FeatureFlagToggle({
  flag,
  field,
  onUpdate,
  isUpdating = false,
  'data-testid': testId
}: FeatureFlagToggleProps) {
  const isChecked = field === 'enabled' ? flag.enabled : (flag.admin_enabled || false);

  const handleToggle = useCallback(async () => {
    if (isUpdating) return;

    // DV-FF-002: Capture reason for audit logging
    const action = isChecked ? 'disable' : 'enable';
    const fieldDisplay = field === 'enabled' ? 'feature' : 'admin override';
    const reason = prompt(
      `Please provide a reason for ${action}ing ${fieldDisplay} for "${flag.name}":`,
      `${action} ${fieldDisplay}`
    );

    // Allow cancellation
    if (reason === null) return;

    try {
      await onUpdate({
        name: flag.name,
        [field]: !isChecked,
        reason: reason.trim() || `${action} ${fieldDisplay}`
      });
    } catch (error) {
      console.error(`Failed to toggle ${field}:`, error);
    }
  }, [flag.name, field, isChecked, isUpdating, onUpdate]);

  return (
    <button
      onClick={handleToggle}
      disabled={isUpdating}
      className={cn(
        "relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2",
        isChecked ? "bg-accent" : "bg-border-default",
        isUpdating && "opacity-50 cursor-not-allowed"
      )}
      style={{
        backgroundColor: isChecked ? 'var(--accent)' : 'var(--border-default)',
        ['--focus-ring' as any]: '0 0 0 2px var(--border-focus)'
      }}
      onFocus={(e) => {
        e.currentTarget.style.boxShadow = 'var(--focus-ring)';
      }}
      onBlur={(e) => {
        e.currentTarget.style.boxShadow = 'none';
      }}
      data-testid={testId}
      aria-label={`${field === 'enabled' ? 'Enable' : 'Admin enable'} ${flag.name}`}
      aria-checked={isChecked}
      role="switch"
    >
      <span
        className={cn(
          "inline-block h-3 w-3 transform rounded-full transition-transform",
          isChecked ? "translate-x-5" : "translate-x-1"
        )}
        style={{
          backgroundColor: 'var(--bg-app)'
        }}
      />
      {isUpdating && (
        <Loader2 className="absolute inset-0 w-3 h-3 m-auto animate-spin" style={{ color: 'var(--fg-muted)' }} />
      )}
    </button>
  );
}

// Feature flag row component
interface FeatureFlagRowProps {
  flag: FeatureFlagWithStatus;
  onUpdate: (update: FeatureFlagUpdate) => Promise<void>;
  onEdit: (flag: FeatureFlagWithStatus) => void;
  isUpdating?: boolean;
  'data-testid'?: string;
}

function FeatureFlagRow({ flag, onUpdate, onEdit, isUpdating, 'data-testid': testId }: FeatureFlagRowProps) {
  const status = flag.effective_status;

  const getStatusIcon = () => {
    if (status.enabled) {
      return <CheckCircle className="w-4 h-4" style={{ color: 'var(--success)' }} />;
    } else if (status.reason.includes('blacklist')) {
      return <XCircle className="w-4 h-4" style={{ color: 'var(--danger)' }} />;
    } else {
      return <AlertTriangle className="w-4 h-4" style={{ color: 'var(--warning)' }} />;
    }
  };

  const formatBrands = (brands: string[]) => {
    if (!brands.length) return '—';
    return brands.join(', ');
  };

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <tr
      className={cn(
        "border-b hover:bg-opacity-5 transition-colors",
        isUpdating && "bg-opacity-50"
      )}
      style={{
        borderColor: 'var(--border-subtle)',
        ['--hover-bg' as any]: 'var(--bg-interactive-hover)',
        backgroundColor: isUpdating ? 'var(--bg-elevated)' : 'transparent',
        opacity: isUpdating ? 0.7 : 1,
        transition: 'background-color 0.2s ease, opacity 0.2s ease',
        animation: isUpdating ? 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' : 'none'
      }}
      onMouseEnter={(e) => {
        if (!isUpdating) {
          e.currentTarget.style.backgroundColor = 'var(--hover-bg)';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = isUpdating ? 'var(--bg-elevated)' : 'transparent';
      }}
      data-testid={testId}
      aria-busy={isUpdating}
      aria-live={isUpdating ? 'polite' : undefined}
    >
      {/* Name */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="font-mono font-medium" style={{ color: 'var(--fg-primary)' }}>
            {flag.name}
          </div>
          {isUpdating && (
            <Loader2 className="w-3 h-3 animate-spin" style={{ color: 'var(--accent)' }} />
          )}
        </div>
        <div className="text-xs mt-1" style={{ color: 'var(--fg-muted)' }}>
          {flag.description}
        </div>
      </td>

      {/* General Enabled Toggle */}
      <td className="px-4 py-3">
        <FeatureFlagToggle
          flag={flag}
          field="enabled"
          onUpdate={onUpdate}
          isUpdating={isUpdating}
          data-testid={`ff-enabled-${flag.name}`}
        />
      </td>

      {/* Admin Enabled Toggle */}
      <td className="px-4 py-3">
        <FeatureFlagToggle
          flag={flag}
          field="admin_enabled"
          onUpdate={onUpdate}
          isUpdating={isUpdating}
          data-testid={`ff-admin-enabled-${flag.name}`}
        />
      </td>

      {/* Effective Status */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          {getStatusIcon()}
          <span className="text-sm" style={{ color: 'var(--fg-primary)' }}>
            {status.enabled ? 'On' : 'Off'}
          </span>
        </div>
        <div className="text-xs mt-1" style={{ color: 'var(--fg-muted)' }}>
          {status.reason}
        </div>
      </td>

      {/* Rollout % */}
      <td className="px-4 py-3">
        <div className="text-sm font-mono" style={{ color: 'var(--fg-primary)' }}>
          {flag.rollout_percentage}%
        </div>
      </td>

      {/* Whitelist */}
      <td className="px-4 py-3">
        <div className="text-xs font-mono max-w-24 truncate" style={{ color: 'var(--fg-secondary)' }}>
          {formatBrands(flag.whitelist_brands)}
        </div>
      </td>

      {/* Blacklist */}
      <td className="px-4 py-3">
        <div className="text-xs font-mono max-w-24 truncate" style={{ color: 'var(--fg-secondary)' }}>
          {formatBrands(flag.blacklist_brands)}
        </div>
      </td>

      {/* Updated At */}
      <td className="px-4 py-3">
        <div className="text-xs" style={{ color: 'var(--fg-muted)' }}>
          {formatDate(flag.updated_at)}
        </div>
      </td>

      {/* Actions */}
      <td className="px-4 py-3">
        <button
          onClick={() => onEdit(flag)}
          className="p-1 rounded hover:bg-opacity-10 transition-colors focus:outline-none focus:ring-2"
          style={{
            color: 'var(--fg-muted)',
            ['--hover-bg' as any]: 'var(--bg-interactive-hover)',
            ['--focus-ring' as any]: '0 0 0 2px var(--border-focus)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--hover-bg)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
          onFocus={(e) => {
            e.currentTarget.style.boxShadow = 'var(--focus-ring)';
          }}
          onBlur={(e) => {
            e.currentTarget.style.boxShadow = 'none';
          }}
          title="Edit flag details"
        >
          <Edit className="w-4 h-4" />
        </button>
      </td>
    </tr>
  );
}

export function FeatureFlagsConsole({
  onLog,
  className,
  'data-testid': testId = 'feature-flags-console'
}: FeatureFlagsConsoleProps) {
  const [filters, setFilters] = useState<FeatureFlagsFilters>({
    search: '',
    enabledOnly: false,
    sortBy: 'name',
    sortOrder: 'asc'
  });

  // DV-FF-002: State for status report and last update tracking
  const [copySuccess, setCopySuccess] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<{
    flagName: string;
    fieldsChanged: string[];
    timestamp: string;
  } | null>(null);

  // DV-FF-002: Row-level updating indicators
  const [updatingFlags, setUpdatingFlags] = useState<Set<string>>(new Set());

  // const [editingFlag, setEditingFlag] = useState<FeatureFlagWithStatus | null>(null);

  // Initialize status report hook
  const { buildFeatureFlagsReport } = useStatusReport();

  // Data fetching
  const { data: adminCheck, isLoading: adminLoading } = useFeatureFlagsAdminCheck();
  const { data: flags, isLoading, error, refetch } = useFeatureFlagsWithStatus();
  const { data: auditEntries } = useFeatureFlagAuditLog(undefined, 20);
  const updateFlagMutation = useUpdateFeatureFlag();

  // Filter and sort flags
  const filteredFlags = useMemo(() => {
    if (!flags) return [];

    // Create a copy to avoid mutating React Query cache
    let filtered = [...flags];

    // Search filter
    if (filters.search.trim()) {
      const search = filters.search.toLowerCase();
      filtered = filtered.filter(flag =>
        flag.name.toLowerCase().includes(search) ||
        flag.description.toLowerCase().includes(search)
      );
    }

    // Enabled only filter - use effective status
    if (filters.enabledOnly) {
      filtered = filtered.filter(flag => flag.effective_status.enabled);
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;

      switch (filters.sortBy) {
        case 'name':
          aVal = a.name;
          bVal = b.name;
          break;
        case 'updated_at':
          aVal = new Date(a.updated_at).getTime();
          bVal = new Date(b.updated_at).getTime();
          break;
        case 'rollout_percentage':
          aVal = a.rollout_percentage;
          bVal = b.rollout_percentage;
          break;
        default:
          aVal = a.name;
          bVal = b.name;
      }

      const comparison = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      return filters.sortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [flags, filters]);

  // DV-FF-002: Handle flag updates with unified error handling and update tracking
  const handleFlagUpdate = useCallback(async (update: FeatureFlagUpdate) => {
    // DV-FF-002: Add row-level updating indicator
    setUpdatingFlags(prev => new Set(prev).add(update.name));

    try {
      await updateFlagMutation.mutateAsync(update);

      // Track last update for status report
      setLastUpdate({
        flagName: update.name,
        fieldsChanged: Object.keys(update).filter(k => k !== 'name'),
        timestamp: new Date().toISOString()
      });

      // Log the change
      onLog?.({
        id: `ff-update-${Date.now()}`,
        ts: new Date().toISOString(),
        level: 'info',
        text: `Feature flag updated: ${update.name}`,
        meta: {
          source: 'feature_flags',
          flag: update.name,
          changes: Object.keys(update).filter(k => k !== 'name'),
          reason: update.reason
        }
      });
    } catch (error) {
      // Convert to demo error format
      const demoError = getDemoError(error);

      // DV-FF-002: Special handling for 409 conflicts
      if (demoError?.status === 409) {
        onLog?.({
          id: `ff-conflict-${Date.now()}`,
          ts: new Date().toISOString(),
          level: 'warn',
          text: `Conflict detected updating ${update.name}: flag was modified by another user. UI has been refreshed with latest data.`,
          meta: {
            source: 'feature_flags',
            flag: update.name,
            error_type: 'conflict',
            status: 409
          }
        });
      } else if (demoError && onLog) {
        const logEntry = logDemoError(demoError);
        onLog({
          ...logEntry,
          meta: {
            ...logEntry.meta,
            source: 'feature_flags',
            flag: update.name
          }
        });
      }
      throw error; // Re-throw for UI handling
    } finally {
      // DV-FF-002: Remove row-level updating indicator
      setUpdatingFlags(prev => {
        const next = new Set(prev);
        next.delete(update.name);
        return next;
      });
    }
  }, [updateFlagMutation, onLog]);

  // DV-FF-002: Status report copy handler
  const handleCopyStatusReport = useCallback(async () => {
    try {
      // Get recent API calls from the API client
      const recentApiCalls = featureFlagsAPI.getRecentApiCalls?.() || [];

      const report = buildFeatureFlagsReport(
        flags || [],
        [], // recentLogs - would be passed from parent
        recentApiCalls,
        lastUpdate || undefined
      );

      await navigator.clipboard.writeText(report);

      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);

      onLog?.({
        id: `ff-status-copy-${Date.now()}`,
        ts: new Date().toISOString(),
        level: 'info',
        text: 'Feature Flags status report copied to clipboard',
        meta: {
          source: 'feature_flags',
          reportSize: report.length,
          flagCount: flags?.length || 0,
          correlation_id: `ff-copy-${Date.now()}`
        }
      });
    } catch (error) {
      onLog?.({
        id: `ff-copy-error-${Date.now()}`,
        ts: new Date().toISOString(),
        level: 'error',
        text: `Failed to copy Feature Flags status report: ${error}`,
        meta: {
          source: 'feature_flags',
          error: error instanceof Error ? error.message : 'Unknown error'
        }
      });
    }
  }, [buildFeatureFlagsReport, flags, lastUpdate, onLog]);

  // DV-FF-002: Unified error handling
  const mainError = useMemo(() => {
    if (!error) return null;
    const demoErr = getDemoError(error);
    if (demoErr && onLog) {
      const logEntry = logDemoError(demoErr);
      onLog({
        ...logEntry,
        meta: {
          ...logEntry.meta,
          source: 'feature_flags'
        }
      });
    }
    return demoErr;
  }, [error, onLog]);

  // Admin access check
  if (adminLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--accent)' }} />
        <span className="ml-2" style={{ color: 'var(--fg-muted)' }}>
          Checking admin access...
        </span>
      </div>
    );
  }

  if (!adminCheck) {
    return (
      <PermissionNotice
        error={{
          type: 'permission_denied',
          status: 403,
          message: 'Feature Flags Console requires admin access',
          userMessage: 'You do not have permission to access the Feature Flags Console',
          remediation: [
            'Verify you are logged in with an admin account',
            'Check that your user profile has admin role set',
            'Contact support if you believe this is an error'
          ],
          endpoint: '/feature-flags-console',
          retryable: false
        }}
        className={className}
        data-testid={`${testId}-permission-denied`}
      />
    );
  }

  return (
    <div className={cn("space-y-6", className)} data-testid={testId}>
      {/* Header */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h2
            className="text-xl font-light flex items-center gap-2"
            style={{ color: 'var(--accent)' }}
          >
            <Settings className="w-5 h-5" />
            Feature Flags Console
          </h2>

          {/* DV-FF-002: Copy Status Report Button */}
          <button
            onClick={handleCopyStatusReport}
            className="flex items-center gap-1 px-2 py-1 rounded text-xs border transition-colors focus:outline-none focus:ring-2"
            style={{
              backgroundColor: 'var(--bg-surface)',
              borderColor: 'var(--border-default)',
              color: 'var(--fg-primary)',
              ['--hover-bg' as any]: 'var(--bg-interactive-hover)',
              ['--focus-ring' as any]: '0 0 0 2px var(--border-focus)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--hover-bg)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--bg-surface)';
            }}
            onFocus={(e) => {
              e.currentTarget.style.boxShadow = 'var(--focus-ring)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.boxShadow = 'none';
            }}
            data-testid="dv-status-copy"
            aria-label="Copy status report to clipboard"
          >
            {copySuccess ? (
              <Check className="w-3 h-3" style={{ color: 'var(--success)' }} />
            ) : (
              <Copy className="w-3 h-3" />
            )}
            Copy Status Report
          </button>
        </div>
        <p
          className="text-sm"
          style={{ color: 'var(--fg-muted)' }}
        >
          Admin-only console for managing feature flags, rollouts, and brand restrictions.
        </p>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4" style={{ color: 'var(--fg-muted)' }} />
            <input
              type="text"
              placeholder="Search flags..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              className="pl-10 pr-3 py-2 rounded border text-sm focus:outline-none focus:ring-2"
              style={{
                backgroundColor: 'var(--bg-surface)',
                borderColor: 'var(--border-default)',
                color: 'var(--fg-primary)',
                ['--focus-ring' as any]: '0 0 0 2px var(--border-focus)'
              }}
              onFocus={(e) => {
                e.currentTarget.style.boxShadow = 'var(--focus-ring)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.boxShadow = 'none';
              }}
              data-testid={`${testId}-search`}
            />
          </div>

          {/* Enabled Only Filter */}
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={filters.enabledOnly}
              onChange={(e) => setFilters(prev => ({ ...prev, enabledOnly: e.target.checked }))}
              className="w-3 h-3 rounded focus:ring-2"
              style={{
                backgroundColor: 'var(--bg-surface)',
                borderColor: 'var(--border-default)',
                color: 'var(--accent)'
              }}
              data-testid={`${testId}-enabled-only`}
            />
            <Filter className="w-3 h-3" style={{ color: 'var(--fg-muted)' }} />
            <span style={{ color: 'var(--fg-muted)' }}>Enabled Only</span>
          </label>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => refetch()}
            disabled={isLoading}
            className="flex items-center gap-1 px-3 py-2 rounded text-sm border transition-colors focus:outline-none focus:ring-2"
            style={{
              backgroundColor: 'var(--bg-surface)',
              borderColor: 'var(--border-default)',
              color: 'var(--fg-primary)',
              ['--hover-bg' as any]: 'var(--bg-interactive-hover)',
              ['--focus-ring' as any]: '0 0 0 2px var(--border-focus)'
            }}
            onMouseEnter={(e) => {
              if (!isLoading) {
                e.currentTarget.style.backgroundColor = 'var(--hover-bg)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--bg-surface)';
            }}
            onFocus={(e) => {
              e.currentTarget.style.boxShadow = 'var(--focus-ring)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.boxShadow = 'none';
            }}
            data-testid={`${testId}-refresh`}
          >
            <RefreshCw className={cn("w-4 h-4", isLoading && "animate-spin")} />
            Refresh
          </button>
        </div>
      </div>

      {/* Feature Flags Table */}
      <div
        className="border rounded-lg overflow-hidden"
        style={{
          backgroundColor: 'var(--bg-surface)',
          borderColor: 'var(--border-default)'
        }}
      >
        {isLoading ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--accent)' }} />
            <span className="ml-2" style={{ color: 'var(--fg-muted)' }}>
              Loading feature flags...
            </span>
          </div>
        ) : mainError ? (
          <div className="p-4">
            {mainError.type === 'permission_denied' && (
              <PermissionNotice
                error={mainError}
                onRetry={refetch}
                data-testid="feature-flags-permission-error"
              />
            )}
            {mainError.type === 'not_found' && (
              <NotFoundNotice
                error={mainError}
                onRetry={refetch}
                data-testid="feature-flags-not-found-error"
              />
            )}
            {mainError.status === 409 && (
              <ConflictNotice
                error={mainError}
                onRetry={refetch}
                data-testid="feature-flags-conflict-error"
              />
            )}
            {(mainError.type === 'server_error' || mainError.type === 'network_error') && (
              <ServerErrorNotice
                error={mainError}
                onRetry={refetch}
                data-testid="feature-flags-server-error"
              />
            )}
          </div>
        ) : filteredFlags.length === 0 ? (
          <div className="p-8 text-center">
            <Eye className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--fg-muted)' }} />
            <div className="text-sm" style={{ color: 'var(--fg-muted)' }}>
              {filters.search || filters.enabledOnly ? 'No flags match your filters' : 'No feature flags found'}
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead
                className="border-b"
                style={{
                  backgroundColor: 'var(--bg-elevated)',
                  borderColor: 'var(--border-subtle)'
                }}
              >
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">
                    <span style={{ color: 'var(--fg-secondary)' }}>Name</span>
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider">
                    <span style={{ color: 'var(--fg-secondary)' }}>Enabled</span>
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider">
                    <span style={{ color: 'var(--fg-secondary)' }}>Admin</span>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">
                    <span style={{ color: 'var(--fg-secondary)' }}>Effective</span>
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider">
                    <span style={{ color: 'var(--fg-secondary)' }}>Rollout %</span>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">
                    <span style={{ color: 'var(--fg-secondary)' }}>Whitelist</span>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">
                    <span style={{ color: 'var(--fg-secondary)' }}>Blacklist</span>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">
                    <span style={{ color: 'var(--fg-secondary)' }}>Updated</span>
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider">
                    <span style={{ color: 'var(--fg-secondary)' }}>Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredFlags.map((flag) => (
                  <FeatureFlagRow
                    key={flag.id}
                    flag={flag}
                    onUpdate={handleFlagUpdate}
                    onEdit={() => {}} // TODO: implement edit functionality
                    isUpdating={updatingFlags.has(flag.name)}
                    data-testid={`${testId}-row-${flag.name}`}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Audit Log Panel */}
      {auditEntries && auditEntries.length > 0 && (
        <div>
          <h3
            className="text-lg font-light mb-4"
            style={{ color: 'var(--accent)' }}
          >
            Recent Changes
          </h3>
          <div
            className="border rounded-lg p-4 space-y-2 max-h-64 overflow-y-auto"
            style={{
              backgroundColor: 'var(--bg-surface)',
              borderColor: 'var(--border-default)'
            }}
          >
            {auditEntries.map((entry) => (
              <div
                key={entry.id}
                className="flex items-center justify-between py-2 px-3 rounded border-l-2"
                style={{
                  backgroundColor: 'var(--bg-elevated)',
                  borderLeftColor: entry.action.includes('enabled') ? 'var(--accent)' : 'var(--fg-muted)'
                }}
              >
                <div className="flex-1">
                  <div className="text-sm font-mono" style={{ color: 'var(--fg-primary)' }}>
                    {entry.feature_name} — {entry.action}
                  </div>
                  {entry.reason && (
                    <div className="text-xs mt-1" style={{ color: 'var(--fg-muted)' }}>
                      {entry.reason}
                    </div>
                  )}
                </div>
                <div className="text-xs" style={{ color: 'var(--fg-muted)' }}>
                  {new Date(entry.created_at).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}